import { useCallback, useRef, useState } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from "react-native";
import { YStack } from "tamagui";

import LoadingSpinner from "@/components/animated/LoadingSpinner";
import { Button } from "@/components/Button";
import { Input } from "@/components/forms/Input";
import { Paragraph } from "@/components/Typography";

const LOAD_MORE_THRESHOLD = 120;

export type ComboBoxItem = {
  label: string;
  value: string;
};

type ComboBoxProps = {
  label?: string;
  placeholder?: string;
  searchText: string;
  onSearchTextChange: (text: string) => void;
  items: ComboBoxItem[];
  onSelect: (item: ComboBoxItem) => void;
  dataTrackingId: string;
  onEndReached?: () => void;
  isFetchingMore?: boolean;
  isLoading?: boolean;
  errorText?: string;
  emptyText?: string;
  testID?: string;
  maxHeight?: number;
};

export default function ComboBox({
  label,
  placeholder,
  searchText,
  onSearchTextChange,
  items,
  onSelect,
  dataTrackingId,
  onEndReached,
  isFetchingMore = false,
  isLoading = false,
  errorText,
  emptyText,
  testID,
  maxHeight = 240,
}: ComboBoxProps) {
  const [open, setOpen] = useState(false);
  const [listHeight, setListHeight] = useState<number | undefined>(undefined);

  const onEndReachedRef = useRef(onEndReached);
  onEndReachedRef.current = onEndReached;
  const isFetchingMoreRef = useRef(isFetchingMore);
  isFetchingMoreRef.current = isFetchingMore;
  const scrollLayoutHeight = useRef(0);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (
        distanceFromBottom < LOAD_MORE_THRESHOLD &&
        !isFetchingMoreRef.current
      ) {
        onEndReachedRef.current?.();
      }
    },
    [],
  );

  const handleContentSizeChange = useCallback(
    (_: number, contentHeight: number) => {
      if (
        !isFetchingMoreRef.current &&
        contentHeight <= scrollLayoutHeight.current
      ) {
        onEndReachedRef.current?.();
      }
    },
    [],
  );

  const showList = open && items.length > 0;
  const showLoading = open && isLoading && listHeight != null;
  const showEmpty =
    open && !isLoading && items.length === 0 && searchText.length > 0;

  return (
    <YStack gap="$100">
      <Input.Controlled
        label={label}
        placeholder={placeholder}
        value={searchText}
        errorText={errorText}
        testID={testID}
        onFocus={() => setOpen(true)}
        onChangeText={(text) => {
          setOpen(true);
          onSearchTextChange(text);
        }}
      />
      {showLoading && (
        <YStack
          height={listHeight}
          maxHeight={maxHeight}
          justifyContent="center"
          alignItems="center"
        >
          <LoadingSpinner size="$200" />
        </YStack>
      )}
      {showList && (
        <YStack
          maxHeight={maxHeight}
          onLayout={(e) => setListHeight(e.nativeEvent.layout.height)}
        >
          <ScrollView
            keyboardShouldPersistTaps="handled"
            onScroll={handleScroll}
            scrollEventThrottle={200}
            onLayout={(e) => {
              scrollLayoutHeight.current = e.nativeEvent.layout.height;
            }}
            onContentSizeChange={handleContentSizeChange}
          >
            <YStack gap="$100">
              {items.map((item) => (
                <Button.Wrapper
                  key={item.value}
                  dataTrackingId={dataTrackingId}
                  testID={testID ? `${testID}:${item.value}` : undefined}
                  onPress={() => {
                    setOpen(false);
                    onSelect(item);
                  }}
                  backgroundColor="$background"
                  borderRadius="$100"
                  borderWidth="$strokeWidth"
                  borderColor="$strokeNeutral"
                  padding="$150"
                  alignItems="flex-start"
                >
                  <Paragraph size="$sm">{item.label}</Paragraph>
                </Button.Wrapper>
              ))}
              {isFetchingMore && (
                <YStack alignItems="center" padding="$150">
                  <LoadingSpinner size="$200" />
                </YStack>
              )}
            </YStack>
          </ScrollView>
        </YStack>
      )}
      {showEmpty && emptyText && (
        <Paragraph size="$sm" color="$textSecondary">
          {emptyText}
        </Paragraph>
      )}
    </YStack>
  );
}
